import { useQuery } from "@tanstack/react-query";
import { meQuery } from "@/lib/api";
import { canComplete } from "@/lib/approval";
/** Property pickers bound to an issue: state, priority, assignee, labels, due date, estimate. */
import { useTranslation } from "react-i18next";
import { ListChecks } from "lucide-react";
import { PRIORITIES, formatDue } from "@/lib/issues";
import { liveStates, liveTasks, patchIssue, type Issue, type ProjectData } from "@/lib/store";
import { TASK_STATUS_COLOR, type TaskStatus } from "@/lib/tasks";
import { errorMessage } from "@/lib/api";
import { toast } from "@/lib/toast";
import { Avatar, PriorityIcon, StateIcon } from "./PropertyIcons";
import { Picker, PropButton } from "./Picker";

import { DatePicker } from "./DatePicker";
import { TagPicker } from "./TagPicker";

type Ctl = { open?: boolean; onOpenChange?: (o: boolean) => void };

async function save(issue: Issue, patch: Parameters<typeof patchIssue>[1]) {
  try {
    await patchIssue(issue, patch);
  } catch (e) {
    toast(errorMessage(e), { tone: "error" });
  }
}

export function StatePicker({ d, issue, compact, ...ctl }: { d: ProjectData; issue: Issue; compact?: boolean } & Ctl) {
  const { t } = useTranslation();
  const me = useQuery(meQuery).data?.user;
  const state = d.states[issue.state_id];
  return (
    <Picker
      {...ctl}
      value={issue.state_id}
      placeholder={t("issue.changeStatus")}
      items={liveStates(d).filter((next) => next.type !== "completed" || canComplete(issue, state?.type ?? "", me?.id)).map((s) => ({ id: s.id, label: s.name, icon: <StateIcon state={s} />, keywords: s.type }))}
      onSelect={(id) => void save(issue, { state_id: id })}
      trigger={
        <PropButton title={t("issue.status")} className={compact ? "w-6 justify-center px-0" : ""}>
          <StateIcon state={state} />
          {!compact && <span className="truncate">{state?.name}</span>}
        </PropButton>
      }
    />
  );
}

export function PriorityPicker({ issue, compact, ...ctl }: { issue: Issue; compact?: boolean } & Ctl) {
  const { t } = useTranslation();
  return (
    <Picker
      {...ctl}
      value={String(issue.priority)}
      placeholder={t("issue.changePriority")}
      items={PRIORITIES.map((p) => ({ id: String(p.value), label: t(`priority.${p.key}`), icon: <PriorityIcon priority={p.value} />, hint: String(p.value) }))}
      onSelect={(id) => void save(issue, { priority: Number(id) })}
      trigger={
        <PropButton title={t("issue.priority")} className={compact ? "w-6 justify-center px-0" : ""}>
          <PriorityIcon priority={issue.priority} />
          {!compact && <span>{t(`priority.${PRIORITIES[issue.priority]?.key ?? "none"}`)}</span>}
        </PropButton>
      }
    />
  );
}

export function AssigneePicker({ d, issue, compact, ...ctl }: { d: ProjectData; issue: Issue; compact?: boolean } & Ctl) {
  const { t } = useTranslation();
  const user = issue.assignee_id ? d.users[issue.assignee_id] : undefined;
  const members = d.members.map((m) => d.users[m.user_id]).filter((u): u is NonNullable<typeof u> => !!u);
  const others = Object.values(d.users).filter((u) => !members.some((m) => m.id === u.id));
  return (
    <Picker
      {...ctl}
      value={issue.assignee_id ?? "none"}
      placeholder={t("issue.assignTo")}
      items={[
        { id: "none", label: t("issue.unassigned"), icon: <Avatar size={16} /> },
        ...[...members, ...others].map((u) => ({ id: u.id, label: u.name, icon: <Avatar user={u} size={16} />, keywords: u.email })),
      ]}
      onSelect={(id) => void save(issue, { assignee_id: id === "none" ? null : id })}
      trigger={
        <PropButton title={t("issue.assignee")} className={compact ? "w-6 justify-center px-0" : ""}>
          <Avatar user={user} size={16} />
          {!compact && <span className="truncate">{user?.name ?? t("issue.unassigned")}</span>}
        </PropButton>
      }
    />
  );
}

export function LabelPicker({ d, issue, compact, ...ctl }: { d: ProjectData; issue: Issue; compact?: boolean } & Ctl) {
  return <TagPicker {...ctl} d={d} value={issue.label_ids} compact={compact} disabled={!d.project.role} onChange={(label_ids) => patchIssue(issue, { label_ids })} />;
}

export function DueDateButton({ issue, compact }: { issue: Issue; compact?: boolean }) {
  const { t, i18n } = useTranslation();
  const due = issue.due_date ? formatDue(issue.due_date, i18n.language) : null;
  const tone = due?.tone === "late" ? "text-danger" : due?.tone === "soon" ? "text-warning" : "";
  return <DatePicker value={issue.due_date} label={t("issue.dueDate")} boundary="end" compact={compact} displayText={due?.text}
    className={`h-6 border-transparent bg-transparent px-1.5 ${tone}`} onChange={(value) => patchIssue(issue, { due_date: value || null })} />;
}

export function EstimatePicker({ issue, ...ctl }: { issue: Issue } & Ctl) {
  const { t } = useTranslation();
  const points = [0, 1, 2, 3, 5, 8, 13];
  return (
    <Picker
      {...ctl}
      value={issue.estimate == null ? "none" : String(issue.estimate)}
      placeholder={t("issue.estimate")}
      items={[{ id: "none", label: t("issue.noEstimate") }, ...points.map((p) => ({ id: String(p), label: `${p}` }))]}
      onSelect={(id) => void save(issue, { estimate: id === "none" ? null : Number(id) })}
      trigger={<PropButton title={t("issue.estimate")}>{issue.estimate == null ? t("issue.noEstimate") : `${issue.estimate} pt`}</PropButton>}
    />
  );
}

export function TaskPicker({ d, issue, compact, ...ctl }: { d: ProjectData; issue: Issue; compact?: boolean } & Ctl) {
  const { t } = useTranslation();
  const task = issue.task_id ? d.tasks[issue.task_id] : undefined;
  const dot = (status: string) => <span className="h-2 w-2 rounded-full" style={{ background: TASK_STATUS_COLOR[status as TaskStatus] ?? "#888" }} />;
  return (
    <Picker
      {...ctl}
      value={issue.task_id ?? "none"}
      placeholder={t("issue.setTask")}
      items={[{ id: "none", label: t("issue.noTask") }, ...liveTasks(d).map((tk) => ({ id: tk.id, label: tk.name, icon: dot(tk.status), keywords: tk.status }))]}
      onSelect={(id) => void save(issue, { task_id: id === "none" ? null : id })}
      trigger={
        <PropButton title={t("issue.task")} className={compact ? "px-1" : ""}>
          {task ? dot(task.status) : <ListChecks className="h-3.5 w-3.5" />}
          {!compact && <span className="truncate">{task?.name ?? t("issue.noTask")}</span>}
        </PropButton>
      }
    />
  );
}
